import { EnvironmentalTargetInfo } from '@ais-ui/websockets';
import React from 'react';
import { useSettings } from '../../Settings/SettingsProvider';
import {
  InfoTooltipFields,
  type TooltipFieldDefinition,
} from './InfoTooltipFields';

interface EnvironmentalInfoTooltipProps {
  environmentalInfo: EnvironmentalTargetInfo;
}

export const EnvironmentalInfoTooltip = ({
  environmentalInfo,
}: EnvironmentalInfoTooltipProps) => {
  const { settings } = useSettings();
  const ribbonSettings = settings['ribbonPlugin.settings'];

  const fields: TooltipFieldDefinition[] = [
    // Air-related (wind)
    {
      key: 'windGust',
      label: 'Wind Gust',
      value: environmentalInfo.windReport?.windGust,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'windDirection',
      label: 'Wind Direction',
      value: environmentalInfo.windReport?.windDirection,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'windGustDirection',
      label: 'Wind Gust Direction',
      value: environmentalInfo.windReport?.windGustDirection,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'avgWindSpeed',
      label: 'Average Wind Speed',
      value: environmentalInfo.windReport?.avgWindSpeed,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    // Air-related (weather)
    {
      key: 'airTemperature',
      label: 'Air Temperature',
      value: environmentalInfo.weatherReport?.airTemperature,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'airPressure',
      label: 'Air Pressure',
      value: environmentalInfo.weatherReport?.airPressure,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'airPressureTrend',
      label: 'Air Pressure Trend',
      value: environmentalInfo.weatherReport?.airPressureTrend,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    {
      key: 'dewPoint',
      label: 'Dew Point',
      value: environmentalInfo.weatherReport?.dewPoint,
      visible: ribbonSettings?.show_methyd_air_related,
    },
    // Water-related (sea state)
    {
      key: 'seaState',
      label: 'Sea State',
      value: environmentalInfo.seaStateReport?.seaState,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'swellDirection',
      label: 'Swell Direction',
      value: environmentalInfo.seaStateReport?.swellDirection,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'swellHeight',
      label: 'Swell Height',
      value: environmentalInfo.seaStateReport?.swellHeight,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'swellPeriod',
      label: 'Swell Period',
      value: environmentalInfo.seaStateReport?.swellPeriod,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'signWaveHeight',
      label: 'Significant Wave Height',
      value: environmentalInfo.seaStateReport?.signWaveHeight,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waterTemperature',
      label: 'Water Temperature',
      value: environmentalInfo.seaStateReport?.waterTemperature,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waterTemperatureDepth',
      label: 'Water Temperature Depth',
      value: environmentalInfo.seaStateReport?.waterTemperatureDepth,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'waveDirection',
      label: 'Wave Direction',
      value: environmentalInfo.seaStateReport?.waveDirection,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    {
      key: 'wavePeriod',
      label: 'Wave Period',
      value: environmentalInfo.seaStateReport?.wavePeriod,
      visible: ribbonSettings?.show_methyd_water_related,
    },
    // Metadata
    {
      key: 'stationName',
      label: 'Station Name',
      value: environmentalInfo.stationName,
      visible: ribbonSettings?.show_methyd_station_name,
    },
    {
      key: 'siteId',
      label: 'Site ID',
      value: environmentalInfo.siteId,
      visible: ribbonSettings?.show_methyd_site_id,
    },
    {
      key: 'owner',
      label: 'Owner',
      value: environmentalInfo.owner,
      visible: ribbonSettings?.show_methyd_owner,
    },
    {
      key: 'dataTimeout',
      label: 'Data Timeout',
      value: environmentalInfo.dataTimeout,
      visible: ribbonSettings?.show_methyd_data_timeout,
    },
    {
      key: 'mmsi',
      label: 'MMSI',
      value: environmentalInfo.mmsi,
      visible: ribbonSettings?.show_methyd_mmsi,
    },
    {
      key: 'latitude',
      label: 'Latitude',
      value: environmentalInfo.latitude,
      visible: ribbonSettings?.show_methyd_latitude,
    },
    {
      key: 'longitude',
      label: 'Longitude',
      value: environmentalInfo.longitude,
      visible: ribbonSettings?.show_methyd_longitude,
    },
    {
      key: 'lastOrigin',
      label: 'Last Origin',
      value: environmentalInfo.lastOrigin,
      visible: ribbonSettings?.show_methyd_last_origin,
    },
  ];

  return <InfoTooltipFields fields={fields} gap="gap-4" />;
};
